import { Observable } from '@reactivex/rxjs';
import { SceneGestures } from '../support';
import { cameraChanges } from '@graphistry/viz/src/client/legacy';
import { SELECT_LABEL } from '@graphistry/viz/src/actions/labels';
import { SCENE_TOUCH_START, SCENE_WHEEL_MOVE } from '@graphistry/viz/src/actions/scene';

import labelStyles from '@graphistry/viz/src/components/labels/styles.less';

const EARTH_CIRCUMFERENCE = SceneGestures.EARTH_CIRCUMFERENCE;

// Multipliers for adaptive threshold in Kepler mode
const KEPLER_ZOOM_THRESHOLD_MULTIPLIER_LARGE = 1; // For mouse wheel (large deltaY)
const KEPLER_ZOOM_THRESHOLD_MULTIPLIER_SMALL = 3; // For trackpad (small deltaY)
const KEPLER_ZOOM_DELTA_THRESHOLD = 40; // Threshold to distinguish large vs small deltaY

export function moveCamera(actions) {

    const moveCameraStarts = SceneGestures.startFromActions(
        actions
            .ofType(SCENE_TOUCH_START, SELECT_LABEL)
            .filter(
                ({ event, selectionType, selectionMask }) =>
                    (!selectionType || (selectionType === 'window' && selectionMask)) &&
                    !event.button && !event.getModifierState('Shift') &&
                    !event.getModifierState("Alt")
            )
    );

    const cameraZooms = SceneGestures.wheelFromActions(
        actions
            .ofType(SCENE_WHEEL_MOVE)
            .filter(({ event }) => !isSelectedLabel(event.target))
        )
        .preventDefault().do((x) => cameraChanges.next(zoomCamera(x)));

    // Use defer+repeat pattern to avoid state accumulation bug with .repeat()
    const cameraMoves = Observable.defer(() =>
        SceneGestures
            .pan(moveCameraStarts.take(1), { delay: 125, radius: { x: Infinity, y: Infinity }})
            .mergeMap((drag) => drag
                .stopPropagation()
                .moveCameraInWorldCoords()
                .do(({ camera }) => camera && cameraChanges.next(camera))
                .publish((moves) => moves.take(1).concat(moves.takeLast(1)))
            )
    ).repeat();

    return cameraMoves.merge(cameraZooms).map(toValuesAndInvalidations);
}

function toValuesAndInvalidations({ falcor, camera, camera: { center: { x, y } } }) {
    return {
        falcor: falcor.withoutDataSource(),
        values: [{
            json: {
                camera: {
                    center: { x, y, auto: false, },
                    controls: { 2: { selected: false } }
                }
            }
        }]
    };
}

/**
 * Calculate the zoom trigger threshold based on deltaY magnitude
 * @param {number} deltaY - The scroll delta from the wheel event
 * @returns {number} The threshold that must be accumulated before triggering zoom
 */
function calculateZoomTriggerThreshold(deltaY) {
    const currentDeltaMagnitude = Math.abs(deltaY);
    const multiplier = currentDeltaMagnitude > KEPLER_ZOOM_DELTA_THRESHOLD
        ? KEPLER_ZOOM_THRESHOLD_MULTIPLIER_LARGE
        : KEPLER_ZOOM_THRESHOLD_MULTIPLIER_SMALL;
    return currentDeltaMagnitude * multiplier;
}

/**
 * Calculate target discrete zoom level based on current level and zoom direction
 * @param {number} currentZoomLevel - Current zoom level (0-20)
 * @param {boolean} isZoomingOut - True if zooming out, false if zooming in
 * @returns {number} Target zoom level
 */
function calculateTargetZoomLevel(currentZoomLevel, isZoomingOut) {
    let targetZoomLevel;
    if (isZoomingOut) {
        targetZoomLevel = Math.floor(currentZoomLevel);
        if (Math.abs(currentZoomLevel - targetZoomLevel) < 0.01) {
            targetZoomLevel = Math.max(0, targetZoomLevel - 1);
        }
    } else {
        targetZoomLevel = Math.ceil(currentZoomLevel);
        if (Math.abs(currentZoomLevel - targetZoomLevel) < 0.01) {
            targetZoomLevel = Math.min(20, targetZoomLevel + 1);
        }
    }
    return targetZoomLevel;
}

/**
 * Calculate viewport dimensions from target zoom level
 * @param {number} targetZoomLevel - Target zoom level (0-20)
 * @param {number} canvasWidth - Canvas width in pixels
 * @param {number} currentAspectRatio - Current width/height ratio
 * @returns {{width: number, height: number}} New viewport dimensions
 */
function calculateViewportFromZoomLevel(targetZoomLevel, canvasWidth, currentAspectRatio) {
    const targetWorldWidthPixels = 256 * Math.pow(2, targetZoomLevel);
    const targetMetersPerPixel = EARTH_CIRCUMFERENCE / targetWorldWidthPixels;
    let newWidth = targetMetersPerPixel * canvasWidth;
    let newHeight = newWidth / currentAspectRatio;

    // Check if target viewport height exceeds Mercator limits
    // If so, calculate and use the lowest zoom level meeting criteria
    if (newHeight > EARTH_CIRCUMFERENCE) {
        const maxAllowedWidth = EARTH_CIRCUMFERENCE * currentAspectRatio;
        const maxMetersPerPixel = maxAllowedWidth / canvasWidth;
        const maxWorldWidthPixels = EARTH_CIRCUMFERENCE / maxMetersPerPixel;
        const maxZoomLevel = Math.log2(maxWorldWidthPixels / 256);

        const cappedWorldWidthPixels = 256 * Math.pow(2, maxZoomLevel);
        const cappedMetersPerPixel = EARTH_CIRCUMFERENCE / cappedWorldWidthPixels;
        newWidth = cappedMetersPerPixel * canvasWidth;
        newHeight = newWidth / currentAspectRatio;
    }

    return { width: newWidth, height: newHeight };
}

function zoomCamera({ event, renderState: { camera, canvas }, isKeplerOpen }) {
    // Calculate the zoom factor as the log of the WheelEvent's deltaY
    // in whichever direction was scrolled. This yields a nice smooth
    // zoom acceleration gradient as the deltaY grows in size from 0.
    const { clientX, clientY, deltaY } = event;
    const zoomDirection = deltaY > 0 ? 1 : -1;
    // log(1) is 0, so take the max of either abs(deltaY) or 1, then add 1
    const zoomDelta = Math.max(Math.abs(deltaY), 1) + 1;
    const zoomFactor = 1 + (zoomDirection * (Math.log(zoomDelta) / 80));
    const screenPos = camera.canvas2ScreenCoords(clientX, clientY, canvas);

    let xoffset = 0;
    let yoffset = 0;
    if (screenPos !== undefined) {
        xoffset = screenPos.x - camera.center.x;
        yoffset = screenPos.y - camera.center.y;
    }

    // Apply zoom changes
    camera.center.x = camera.center.x + xoffset * (1.0 - zoomFactor);
    camera.center.y = camera.center.y + yoffset * (1.0 - zoomFactor);
    camera.zoom = camera.zoom * zoomFactor;
    camera.width = camera.width * zoomFactor;
    camera.height = camera.height * zoomFactor;

    // If Kepler is open, calculate viewport based on discrete zoom levels (0-20)
    if (isKeplerOpen) {
        // Initialize accumulator if it doesn't exist
        if (camera.zoomAccumulator === undefined) {
            camera.zoomAccumulator = 0;
        }

        const zoomTriggerThreshold = calculateZoomTriggerThreshold(deltaY);
        camera.zoomAccumulator += deltaY;

        // Only apply zoom when accumulated delta exceeds threshold
        if (Math.abs(camera.zoomAccumulator) < zoomTriggerThreshold) {
            return camera;
        }

        // Calculate canvas dimensions in pixels, accounting for pixel ratio
        const canvasWidth = canvas ? canvas.clientWidth * camera.pixelRatio : 1920;

        // Calculate current zoom level from viewport width
        const metersPerPixel = camera.width / canvasWidth;
        const worldWidthPixels = EARTH_CIRCUMFERENCE / metersPerPixel;
        const currentZoomLevel = Math.log2(worldWidthPixels / 256);

        // Determine new zoom level and viewport dimensions
        const isZoomingOut = camera.zoomAccumulator > 0;
        const targetZoomLevel = calculateTargetZoomLevel(currentZoomLevel, isZoomingOut);
        const viewport = calculateViewportFromZoomLevel(targetZoomLevel, canvasWidth, camera.width / camera.height);

        // Calculate zoom factor to reach target width
        const discreteZoomFactor = camera.width / viewport.width;

        camera.width = viewport.width;
        camera.height = viewport.height;

        // Apply discrete zoom
        camera.zoom = camera.zoom * discreteZoomFactor;

        // Reset accumulator after applying zoom
        camera.zoomAccumulator = 0;

        SceneGestures.clampKeplerCamera(camera);
    }

    return camera;
}

function isSelectedLabel(_node) {
    let node = _node;
    return (node = findNode(node, labelStyles['label-contents'])) &&
          (node = findNode(node, labelStyles['label']))
        ? hasClass(node, labelStyles['clicked'])
        : false;
}

function hasClass(node, className) {
    return node.nodeType === 1 && node.classList && node.classList.contains(className);
}

function findNode(_node, className) {
    let node = _node;
    do {
        if (hasClass(node, className)) {
            return node;
        }
    } while ((node = node['parentNode']) && node.nodeType !== 9);
    return null;
}
